import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";

export type StudentAnnouncement = {
  id: string;
  title: string;
  body: string;
  tone: "info" | "success" | "warning";
  showAsBanner: boolean;
  linkUrl: string | null;
  linkLabel: string | null;
  createdAt: string;
  isRead: boolean;
};

type AnnouncementsResponse = { announcements: StudentAnnouncement[]; unreadCount: number };

const KEY = ["announcements"] as const;

/**
 * The signed-in student's announcements — one cached query shared by the
 * header bell and the dashboard banner, so reading one updates both.
 */
export function useAnnouncements(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api.get<AnnouncementsResponse>("/api/v1/announcements"),
    enabled,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Marks announcements as read, updating the cache immediately. */
export function useMarkAnnouncementsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post("/api/v1/announcements/read", { ids }),
    onMutate: (ids) => {
      queryClient.setQueryData<AnnouncementsResponse>(KEY, (current) => {
        if (!current) return current;
        const announcements = current.announcements.map((a) => (ids.includes(a.id) ? { ...a, isRead: true } : a));
        return { announcements, unreadCount: announcements.filter((a) => !a.isRead).length };
      });
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
